
// Profile tabs with grants integration
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { FileText, MessageCircle, Video, Sprout, Radio, DollarSign, Users } from "lucide-react";
import { PostsTab } from "./PostsTab";
import { MessagesTab } from "./MessagesTab";
import { VideoResponsesTab } from "./VideoResponsesTab";
import { CropPlanTab } from "./CropPlanTab";
import { ISOCastsTab } from "./ISOCastsTab";
import { GrantsTab } from "./GrantsTab";
import { JoinedCommunities } from "./profile/JoinedCommunities";
import { Profile } from "@/hooks/useProfile";
import { Cast } from "@/hooks/useCasts";
import { Post } from "@/hooks/posts/types";

interface ProfileTabsProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  profile: Profile;
  posts: Post[];
  casts: Cast[];
  userCropPlans: any[];
  onTimelapseClick: (crop: string) => void;
  onPostClick: (post: Post) => void;
  onRemoveCrop: (cropId: string, cropName: string) => void;
  onToggleSolved?: (castId: string) => void;
  onEditPost?: (post: Post) => void;
  onDeletePost?: (postId: string) => void;
  isEditing?: boolean;
  isOwner?: boolean;
}

export const ProfileTabs = ({ 
  activeTab, 
  setActiveTab, 
  profile, 
  posts, 
  casts,
  userCropPlans, 
  onTimelapseClick, 
  onPostClick, 
  onRemoveCrop,
  onToggleSolved,
  onEditPost,
  onDeletePost,
  isEditing = false,
  isOwner = false
}: ProfileTabsProps) => {
  console.log('🔍 ProfileTabs - onToggleSolved prop:', !!onToggleSolved);
  console.log('🔍 ProfileTabs - casts count:', casts.length);
  // Mock data for messages
  const mockMessages = [
    {
      id: 1,
      from: "Valley Organic Farm",
      content: "Great post about your tomato harvest! What variety are you growing?",
      timestamp: "2 hours ago",
      unread: true
    },
    {
      id: 2,
      from: "Green Leaf Gardens",
      content: "Thanks for the advice on pest control. It really helped!",
      timestamp: "1 day ago",
      unread: false
    }
  ];

  return (
    <Card>
      <CardContent className="p-0">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 gap-y-1 bg-green-50 h-auto py-2 text-xs">
            <TabsTrigger 
              value="communities" 
              className="flex items-center gap-1 text-xs data-[state=active]:bg-green-600 data-[state=active]:text-white"
            >
              <Users size={14} />
              Communities
            </TabsTrigger>
            <TabsTrigger 
              value="posts" 
              className="flex items-center gap-1 text-xs data-[state=active]:bg-green-600 data-[state=active]:text-white"
            >
              <FileText size={14} />
              Posts
            </TabsTrigger>
            <TabsTrigger 
              value="iso-casts" 
              className="flex items-center gap-1 text-xs data-[state=active]:bg-green-600 data-[state=active]:text-white"
            >
              <Radio size={14} />
              ISO Casts
            </TabsTrigger>
            <TabsTrigger 
              value="grants" 
              className="flex items-center gap-1 text-xs data-[state=active]:bg-green-600 data-[state=active]:text-white"
            >
              <DollarSign size={14} />
              Grants
            </TabsTrigger>
            <TabsTrigger 
              value="messages" 
              className="flex items-center gap-1 text-xs data-[state=active]:bg-green-600 data-[state=active]:text-white col-start-1"
            >
              <MessageCircle size={14} />
              Messages
            </TabsTrigger>
            <TabsTrigger 
              value="video-responses" 
              className="flex items-center gap-1 text-xs data-[state=active]:bg-green-600 data-[state=active]:text-white"
            >
              <Video size={14} />
              Video Responses
            </TabsTrigger>
            <TabsTrigger 
              value="crop-plan" 
              className="flex items-center gap-1 text-xs data-[state=active]:bg-green-600 data-[state=active]:text-white"
            >
              <Sprout size={14} />
              Crop Plan
            </TabsTrigger>
          </TabsList>

          <TabsContent value="communities" className="p-3 mt-0">
            <JoinedCommunities profileId={profile.id} isOwner={isOwner} />
          </TabsContent>

          <TabsContent value="posts" className="p-3 mt-0">
            <PostsTab 
              profile={profile}
              posts={posts}
              casts={[]}
              onTimelapseClick={onTimelapseClick}
              onPostClick={onPostClick}
              onToggleSolved={onToggleSolved}
              onEditPost={onEditPost}
              onDeletePost={onDeletePost}
            />
          </TabsContent>

          <TabsContent value="iso-casts" className="p-3 mt-0">
            <ISOCastsTab 
              casts={casts}
              onToggleSolved={onToggleSolved}
            />
          </TabsContent>

          <TabsContent value="grants" className="p-3 mt-0">
            <GrantsTab 
              profile={profile}
            />
          </TabsContent>

          <TabsContent value="messages" className="p-3 mt-0">
            <MessagesTab messages={mockMessages} />
          </TabsContent>

          <TabsContent value="video-responses" className="p-3 mt-0">
            <VideoResponsesTab 
              profile={profile}
            />
          </TabsContent>

          <TabsContent value="crop-plan" className="p-3 mt-0">
            <CropPlanTab 
              cropPlans={userCropPlans}
              onRemoveCrop={onRemoveCrop}
              isEditing={isEditing}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
